import { inject, injectable } from 'inversify';
import Resumable from 'resumablejs';
import { Observer, ReplaySubject, firstValueFrom, of, retry, throwError } from 'rxjs';

import { delay, mergeMap } from 'rxjs/operators';
import { DropboxFile } from '../models/dropbox-file';
import { FileUploadRequestMetadata } from '../models/file-upload-request-metadata';
import { GoogleDriveFile } from '../models/google-drive-file';
import { Logger } from '../models/logger';
import { UploadCancelledError, UploadInProgressFile, isUploadCancelled } from '../models/upload-in-progress-file';
import { GAContants } from '../types/ga.types';
import { OriginalInputFile, PSUploadTypes, WebsocketResponse } from '../types/types';
import { getFileMimeType } from '../utils/file';
import { get, isString, parseInt } from '../utils/lodash-min';
import { MetricErrorClass, errorClass, hostOnly, sizeBucket, statusOf } from '../utils/metric-attrs';
import { emitMetric } from '../utils/metric-emit';
import { MetricEventName } from '../types/metric-events';
import { ProcessorMetadataUtils } from '../utils/processor-metadata.utils';
import { ClientError, NetworkError, ServiceError } from '../utils/request.utils';
import { StringUtils } from '../utils/string-utils';
import { GAService } from './analytics.service';
import { AuthService } from './auth.service';
import { HelperService } from './helper.service';
import { HttpProgressEvent, LightHttp } from './light-http.service';
import { UploadOptions } from '../types/file-upload.types';
import { LoginService } from './login.service';
import { RefreshService } from './refresh.service';
import { SettingsService } from './settings.service';
import { UIContext } from './ui-context';
import { WebSocketService } from './websocket.service';
import {
    FINALIZE_MISSING_MARKER, UPLOAD_BLOCKED_COPY, UPLOAD_FAILED_FALLBACK, describeResponseBody, uploadFailureNotice,
} from '../utils/upload-failure';

// fu-r2: these live in `utils/upload-failure.ts` now (one copy table for the service and the manager); re-exported so
// existing imports keep working.
export { FINALIZE_MISSING_MARKER, UPLOAD_BLOCKED_COPY, UPLOAD_BLOCKED_TITLE, isUploadBlockedOnTheWay } from '../utils/upload-failure';



/** Closed set for the FILE_UPLOAD_* `kind` attribute (METRICS.md §4.2 `xc_upload`). */
export type UploadKind = 'local_single' | 'local_chunk' | 'url' | 'drive';

/**
 * IMPORTANT!: All files that are uploaded but be saved to fileUploadMap.<route_path> so that when WS recieves a message,
 * We can update corresponding FileUpload with the data.
 */

/**
 * Review 7 S1-10: an empty (0-byte) file is refused by the page, never by a native `alert()`. resumable.js's default
 * `minFileSizeErrorCallback` alerted "… is too small" (node_modules/resumablejs/resumable.js:78-80) and then never fired
 * `fileAdded`, so the upload promise never settled. The named error takes the upload's normal failure path: not
 * retryable (`ConnectivityUtils.isRetryableUploadError`), the row is marked failed, and `fileSelected` shows it through
 * the page's own error dialog (`uiContext.handleError`: title = name, text = message) or its quiet notice.
 */
export const EMPTY_FILE_ERROR_NAME = 'File is empty';
export const FILE_TOO_LARGE_ERROR_NAME = 'File is too large';

export function emptyFileError(fileName: string | null | undefined): Error {
    const err = new Error(`"${fileName || 'This file'}" is empty (0 bytes), so there is nothing to convert. Pick the file again, or choose a different one.`);
    err.name = EMPTY_FILE_ERROR_NAME;
    return err;
}

/**
 * What a chunk upload rejects with when a chunk ends in error (UPLOAD-FINALIZE.md, 2026-10-03).
 *
 * resumable.js hands its `error` event only the failing chunk's `responseText` -- '' when the request never got an
 * answer -- and the service used to reject with that bare string. `ConnectivityUtils.isRetryableUploadError` reads a
 * string as "not retryable", so a dropped connection skipped the edge re-resolve that `uploadWithRetry` exists for,
 * `handleError` showed it as "Error / Something went wrong!", and the metric could not tell it from anything else.
 *
 * `status` is the failing chunk's XHR status (see `failedChunkStatus`): 0 = no answer (offline, connection reset,
 * blocked before the edge, CORS-hidden) -> NetworkError, retryable on another edge; 5xx -> ServiceError, retryable;
 * anything else -> ClientError, not retried (resumable.js only gives up early on the statuses in `permanentErrors`,
 * which are answers about the request, and another edge would answer the same). `undefined` = status unknown: an empty
 * body is still a lost connection, a body is treated as a refusal.
 */
export function chunkFailureError(message: unknown, status: number | undefined): Error {
    const text = typeof message === 'string' ? message : '';
    if (status === 0 || (status === undefined && text === '')) {
        const err = new NetworkError('The connection to the upload server was lost while the file was uploading.');
        (err as any).status = 0;
        return err;
    }
    const detail = serverMessage(text);
    if (status !== undefined && status >= 500) {
        return new ServiceError(detail || `The upload server failed (${status}).`, status);
    }
    if (!detail && isForeignPage(text)) {
        // Upload-finalize review, point 5 (fu-finalize): a 4xx whose body is an HTML page that is not one of our own
        // stack's pages is not ironman's answer (it answers JSON, or FIX-7's plain text). It is something on the way --
        // a corporate filter's block page (prod: "Internet Security by Zscaler", 8 chunk errors in 3 days) -- and "The
        // upload server refused the file" blamed the wrong party. Not retried on another edge: the same filter answers.
        const err = new ClientError(UPLOAD_BLOCKED_COPY, undefined, status);
        (err as any).blockedOnTheWay = true;
        return err;
    }
    return new ClientError(detail || (status ? `The upload server refused the file (${status}).` : 'The upload server refused the file.'), undefined, status);
}

/**
 * Pages our own stack can answer a chunk POST with: nginx's built-in error page (`<hr><center>nginx</center>`, e.g. a 400
 * or 413 from the edge's own nginx), Spring's Whitelabel page, and Cloudflare's error / challenge pages (some xconvert
 * hosts sit behind it). Those keep the "refused" message: blaming the user's network for them would be just as wrong.
 */
const OWN_STACK_PAGE = /<center>\s*nginx|Whitelabel Error Page|cloudflare/i;

/** True for a body that is a page (HTML or XML markup), not an API answer, and not one of [OWN_STACK_PAGE]. */
function isForeignPage(text: string): boolean {
    const t = text.trimStart();
    return t.charAt(0) === '<' && !OWN_STACK_PAGE.test(t);
}

/**
 * The `xhr.timeout` for one chunk POST (fu-finalize; upload-finalize review: resumable.js's default `xhrTimeout: 0`
 * means a request on a dead connection never ends -- no error, no retry, the row stalls and the upload row stays
 * STARTED for good).
 *
 * The XHR timeout is TOTAL time for the request, and the clock starts at `send()`: it includes waiting for one of the
 * browser's 6 HTTP/1.1 sockets per host (the edge nginx does not speak h2), which up to 4 files x `simultaneousUploads`
 * (8 on prod) chunk requests share, plus the body, plus the edge's answer. A value tuned for "is it stalled?" would
 * kill legitimate slow uploads, and a killed request restarts its chunk from byte 0 -- so this is a backstop against
 * "forever", not a stall detector (a no-progress watchdog would be; UPLOAD-FINALIZE.md §9):
 *  - 5 min for the edge to answer: /v2/edge's nginx `proxy_read_timeout` / `proxy_send_timeout` are 300 s, so any answer
 *    the edge will ever give (even its own 504) arrives first;
 *  - plus the chunk's bytes at 4 KB/s per request (32 kbit/s; with 6 requests in parallel about 200 kbit/s of uplink);
 *  - never under 10 min (covers queueing behind a batch of small chunks).
 * 100 KB / 512 KB / 1 MB chunks: 10 min; 2 MB: ~13.5 min; 5 MB: ~26 min. After it fires, resumable.js retries the chunk
 * (`maxChunkRetries`); a request that still cannot finish fails as status 0 -> `NetworkError` -> the next edge.
 *
 * `override` is `settings.userSettings.chunkXhrTimeoutMs`: a number >= 0 replaces the computed value, and 0 switches the
 * timeout off (resumable.js's own default) -- the rollback without a deploy.
 */
export function chunkXhrTimeoutMs(chunkBytes: number, override?: number): number {
    if (typeof override === 'number' && Number.isFinite(override) && override >= 0) return Math.round(override);
    const EDGE_ANSWER_MS = 300_000;
    const FLOOR_MS = 600_000;
    const MIN_BYTES_PER_MS = 4096 / 1000;
    return Math.max(FLOOR_MS, EDGE_ANSWER_MS + Math.ceil(chunkBytes / MIN_BYTES_PER_MS));
}

/** The `message` of an ironman JSON error body, or '' (HTML pages from proxies and nginx are never shown). */
function serverMessage(text: string): string {
    if (!text || text.charAt(0) !== '{') return '';
    try {
        const m = JSON.parse(text)?.message;
        return typeof m === 'string' && m.length <= 300 ? m : '';
    } catch {
        return '';
    }
}

/**
 * The HTTP status of the chunk that just failed, read from the chunk objects of `chunks` -- the file's chunk array as
 * it was at `fileAdded`. resumable.js 1.1.0 replaces `file.chunks` with [] BEFORE it fires `fileError`, so the array
 * has to be kept from earlier; the failing chunk is the one whose XHR finished (readyState 4) without a 200/201
 * (in-flight siblings were aborted, which nulls their XHR). `undefined` when no such chunk is found.
 */
function failedChunkStatus(chunks: any[]): number | undefined {
    for (const c of chunks) {
        const xhr = c?.xhr;
        if (xhr && xhr.readyState === 4 && xhr.status !== 200 && xhr.status !== 201) {
            return typeof xhr.status === 'number' ? xhr.status : undefined;
        }
    }
    return undefined;
}

function fileTooLargeError(fileName: string | null | undefined, limitMb: number): Error {
    const err = new Error(`"${fileName || 'This file'}" is larger than the ${limitMb} MB upload limit.`);
    err.name = FILE_TOO_LARGE_ERROR_NAME;
    return err;
}

@injectable()
export class FileUploadService {
    logger = Logger.getLogger('FileUploadService');

    maxFileSizeInBytes = 600 * 1000 * 1000;
    maxFileSizeInMB = 600;
    concurrentProcessLimit = 4;
    chunkSize = 1 * 1024 * 1024 + 1;
    simultaneousUploads = 6;
    chunkUploadEnabled = true
    chunkSize1MB = 0;
    chunkSize10MB = 0;
    chunkSize100MB = 0;
    chunkSize500MB = 0;
    chunkSizeLargeFiles = 0;
    /** `settings.userSettings.chunkXhrTimeoutMs`; undefined = computed per chunk ([chunkXhrTimeoutMs]), 0 = no timeout. */
    chunkXhrTimeoutMs: number | undefined = undefined;

    /**
     * The live Resumable per chunk upload, keyed by the `uuid` query the edge sees (R14-SH14). Retained so
     * `cancelChunkUpload` can reach `r.cancel()`; an entry lives from construction to the first of
     * complete / error / cancel. Created lazily: the service specs build this class with
     * `Object.create(FileUploadService.prototype)`, which runs no field initialiser.
     */
    private activeChunkUploads: Map<string, { resumable: Resumable; cancel: () => void }> | undefined;
    private get chunkUploads() {
        if (!this.activeChunkUploads) this.activeChunkUploads = new Map();
        return this.activeChunkUploads;
    }

    private _onFileUploadStart = new ReplaySubject<{ dispatchedAt: Date }>(1);
    get onFileUploadStart() {
        return this._onFileUploadStart;
    }

    private _onFileSelected = new ReplaySubject<{ dispatchedAt: Date }>(1);
    get onFileSelected() {
        return this._onFileSelected;
    }

    public fileSelected() {
        this._onFileSelected.next({ dispatchedAt: new Date() });
    }

    // private fileUploadMap: Map<UniqueProcessType, FileUpload[]> = new Map();
    // private fileUploadsChanged = new Subject<FileUpload>();
    // get onFileUploadChanged() {
    //     return this.fileUploadsChanged.asObservable();
    // }


    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(WebSocketService) private wsService: WebSocketService,
        @inject(GAService) private gaService: GAService,
        @inject(UIContext) private uiContext: UIContext,
        @inject(AuthService) private authService: AuthService,
        @inject(HelperService) private helperService: HelperService,
        @inject(SettingsService) private settingsService: SettingsService,
        @inject(LoginService) private loginService: LoginService,
        @inject(RefreshService) private refreshService: RefreshService,
    ) {

    }

    initialize() {
        this.loginService.onSessionDetails.subscribe({
            next: (sessionDetails) => {
                this.maxFileSizeInBytes = ((sessionDetails && sessionDetails.fileSizeLimitMB) || 400) * 1000 * 1000;
                this.maxFileSizeInMB = (sessionDetails && sessionDetails.fileSizeLimitMB) || 400;
                this.concurrentProcessLimit = (sessionDetails && sessionDetails.numberOfConcurrentProcessLimit) || 4;
            }
        });
        this.settingsService.userSettings.subscribe({
            next: (userSettings) => {
                this.chunkSize = get(userSettings, 'chunkSize', 40 * 1024 * 1024 + 1);
                this.chunkUploadEnabled = get(userSettings, 'chunkUploadEnabled', true);
                this.simultaneousUploads = get(userSettings, 'simultaneousUploads', 2);
                this.chunkSize1MB = get(userSettings, 'chunkSize1MB', 0);
                this.chunkSize10MB = get(userSettings, 'chunkSize10MB', 0);
                this.chunkSize100MB = get(userSettings, 'chunkSize100MB', 0);
                this.chunkSize500MB = get(userSettings, 'chunkSize500MB', 0);
                this.chunkSizeLargeFiles = get(userSettings, 'chunkSizeLargeFiles', 0);
                this.chunkXhrTimeoutMs = get(userSettings, 'chunkXhrTimeoutMs', undefined);
            }
        });

        // this.fileManagerService.onFileUploadRemove()
        //     .subscribe((fileUpload) => {
        //         this.fileUploadMap.forEach(fileUploads => {
        //             const index = fileUploads.findIndex(f => f.id === fileUpload.id);
        //             if (index > -1) {
        //                 fileUploads.splice(index, 1);
        //             }
        //         });
        //         this.notify();
        //     });

        // this.fileManagerService.registerActiveFileUploadsProvider(this);
    }

    // public notify(fileUpload?: FileUpload) {
    //     this.fileUploadsChanged.next(fileUpload);
    // }

    // public getFileUploadArray(processType: UniqueProcessType) {
    //     if (!this.fileUploadMap.get(processType)) {
    //         this.fileUploadMap.set(processType, new Array());
    //     }
    //     return this.fileUploadMap.get(processType);
    // }

    // public getFileUploadMap() {
    //     return this.fileUploadMap;
    // }

    // public getFileUploads(): FileUpload[] {
    //     const fileArray = [];
    //     const fileMap: Map<UniqueProcessType, FileUpload[]> = this.getFileUploadMap();
    //     fileMap.forEach((fileUploads, uniqueProcessType) => {
    //         fileUploads.forEach(fileUpload => {
    //             fileArray.push(fileUpload);
    //         });
    //     });
    //     return fileArray;
    // }

    // public getFileUpload(_uuid: string): FileUpload {
    //     let item;
    //     this.fileUploadMap.forEach((fileUploads, key) => {
    //         fileUploads.forEach(fileUpload => {
    //             if (fileUpload.id === _uuid) {
    //                 item = fileUpload;
    //             }
    //         });
    //     });
    //     return item;
    // }

    private notifyFileUploadStart() {
        this._onFileUploadStart.next({ dispatchedAt: new Date() });
    }

    /**
     * Abort the chunk upload the edge knows as `uploadUUID` (R14-SH14). Idempotent; false when nothing by
     * that id is in flight (finished, failed, never chunked, or already cancelled). Consumers do not call
     * this directly -- `UploadInProgressFile.cancel()` does, through the canceller bound in
     * `chunkUploadSingleFile` -- because the in-progress file's `uuid` and the edge's upload `uuid` are
     * different ids.
     */
    public cancelChunkUpload(uploadUUID: string): boolean {
        const handle = this.chunkUploads.get(uploadUUID);
        if (!handle) return false;
        handle.cancel();
        return true;
    }

    /**
     * FILE_UPLOAD_START / _COMPLETE / _ERROR.
     *
     * These three were defined in `MetricEventName` since March and never
     * emitted, which is why the 29 % of CONVERT_START sessions that never reach
     * FILE_DOWNLOAD has no explanation: an upload that dies before the convert
     * click leaves no trace at all today. Every attribute is bounded
     * (METRICS.md §3) — a size BUCKET not a byte count, the edge HOST not the
     * URL, an errorClass enum not the exception message.
     *
     * `edgeHost` deliberately matches the attribute THUMBNAIL_LOAD_FAILED
     * already carries, so "is one edge bad today" is a single query across both.
     */
    private emitUploadEvent(
        event: MetricEventName,
        base: { kind: UploadKind, size: number, endpoint: string, attempt?: number },
        extra: Record<string, string | number | boolean> = {},
    ) {
        emitMetric(event, {
            kind: base.kind,
            sizeBucket: sizeBucket(base.size),
            edgeHost: hostOnly(base.endpoint),
            retries: base.attempt || 0,
            ...extra,
        });
    }

    /**
     * FILE_UPLOAD_ERROR attributes for a thrown upload failure.
     *
     * `finalize_missing` is asserted by the chunk finalize path (see
     * `updateFileUploadFileOriginalInputFilesAndResolve`): the `original-input-files`
     * GET answering with no data after 3 retries is the measured east-32 prod
     * bug, and it is invisible in a status code because the request itself
     * succeeded. It is the reason this class exists rather than folding into
     * `unknown`.
     */
    private uploadErrorAttributes(err: any): { status: number, errorClass: MetricErrorClass } {
        const status = statusOf(err);
        const hint: MetricErrorClass | undefined = err?.[FINALIZE_MISSING_MARKER] ? 'finalize_missing' : undefined;
        return { status, errorClass: errorClass(status, hint, err?.message) };
    }

    public async uploadFileFromURL(
        fileWithMetadata: UploadInProgressFile<DropboxFile | GoogleDriveFile | File>,
        progressObserver: Observer<any>,
        uploadOptions?: UploadOptions,
    ): Promise<OriginalInputFile> {
        this.notifyFileUploadStart();
        const uploadRequestMetadata: FileUploadRequestMetadata = fileWithMetadata.getMetadata();
        if (uploadRequestMetadata.processorMetadataList[0].uploadDomain.indexOf('localhost') !== -1) {
            this.uiContext.errorToast('Error', 'Running in Test Mode.', false, false);
        }
        if (!uploadOptions?.suppressGAStart) {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.NETWORK,
                GAContants.EventAction['FILE-UPLOAD-START'],
                GAContants.EventLabel.CONVERT,
                `${fileWithMetadata.metadata.file.name}`,
            );
        }
        if (uploadRequestMetadata.processorMetadataList[0].uploadType === PSUploadTypes.SERVER) {
            const progressCallback = (_fileWithMetadata) => {
                let recievedSize = 0;
                const localFileWithMetadata: UploadInProgressFile<any> = _fileWithMetadata;
                return (data) => {
                    this.logger.trace7({ message: 'URL file upload progress response', data: data });
                    if (data.uuid === localFileWithMetadata.getFile().name) {
                        recievedSize = parseFloat(data.data) > recievedSize ? parseFloat(data.data) : recievedSize;
                        const progress = 100 * recievedSize / localFileWithMetadata.getFile().size;

                        if (recievedSize === localFileWithMetadata.getFile().size || parseInt(progress + '', 10) === 100) {
                            localFileWithMetadata.setComplete();
                        }

                        if (data.processStageSubState === 'completed') {
                            localFileWithMetadata.setComplete();
                            this.gaService.send(
                                GAContants.HitType.EVENT,
                                GAContants.EventCategory.NETWORK,
                                GAContants.EventAction['FILE-UPLOAD-END'],
                                GAContants.EventLabel.CONVERT,
                                `${localFileWithMetadata.metadata.file.name}`,
                            );
                            this.wsService.unregisterCallback(removableCallback);
                        }

                        if (!localFileWithMetadata.getComplete()) {
                            localFileWithMetadata.setProgress(parseFloat(parseFloat(progress + '').toFixed(2)));
                        } else {
                            localFileWithMetadata.setProgress(parseFloat(parseFloat('100').toFixed(2)));
                        }
                    }
                };
            };
            const removableCallback = progressCallback(fileWithMetadata);
            this.wsService.registerCallback(removableCallback);
            const urlUploadEndpoint = ProcessorMetadataUtils.getUploadEndpoint(uploadRequestMetadata.processorMetadataList[0]) + '/url';
            const metricBase = {
                kind: 'url' as UploadKind,
                size: fileWithMetadata.getFile().size,
                endpoint: urlUploadEndpoint,
                attempt: uploadOptions?.attempt,
            };
            const startedAt = Date.now();
            this.emitUploadEvent(MetricEventName.FILE_UPLOAD_START, metricBase);
            let result: any;
            try {
                result = await firstValueFrom(this.http.post(urlUploadEndpoint, fileWithMetadata.getFile(), {
                    params: {
                        wsId: this.wsService.getId(),
                        wsDomain: this.wsService.getDomain(),
                        safeFilename: fileWithMetadata.metadata.processorMetadataList[0].fileMetadata.safeName,
                    },
                }));
            } catch (err) {
                this.emitUploadEvent(MetricEventName.FILE_UPLOAD_ERROR, metricBase, {
                    ms: Date.now() - startedAt,
                    ...this.uploadErrorAttributes(err),
                });
                this.wsService.unregisterCallback(removableCallback);
                throw err;
            }
            // `ms` here is the edge's ACK of the fetch request, not the whole
            // byte transfer — that runs server-side and reports over the
            // websocket progress callback above. Named the same as the local
            // path on purpose: both answer "how long until the user's file is
            // accepted", which is the step the funnel loses people at.
            this.emitUploadEvent(MetricEventName.FILE_UPLOAD_COMPLETE, metricBase, {
                ms: Date.now() - startedAt,
            });

            const info = {
                ftype: getFileMimeType(fileWithMetadata.getFile()),
                fname: fileWithMetadata.getFile().name,
                fsize: fileWithMetadata.getFile().size,
                flastModified: fileWithMetadata.getFile().lastModified,
            };
            const originalInputFiles = result.originalInputFiles[0];
            originalInputFiles.metadata = info;
            return originalInputFiles;
        }
        throw new Error(`Unsupported upload type for URL upload: ${uploadRequestMetadata.processorMetadataList[0].uploadType}`);
    }

    public async uploadFileFromGoogleDrive(
        fileWithMetadata: UploadInProgressFile<File>,
        progressObserver: Observer<any>,
        uploadOptions?: UploadOptions,
    ): Promise<OriginalInputFile> {
        this.notifyFileUploadStart();
        const uploadRequestMetadata: FileUploadRequestMetadata = fileWithMetadata.getMetadata();
        if (uploadRequestMetadata.processorMetadataList[0].uploadDomain.indexOf('localhost') !== -1) {
            this.uiContext.errorToast('Error', 'Running in Test Mode.', false, false);
        }
        if (!uploadOptions?.suppressGAStart) {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.NETWORK,
                GAContants.EventAction['FILE-UPLOAD-START'],
                GAContants.EventLabel.CONVERT,
                `${fileWithMetadata.metadata.file.name}`,
            );
        }
        if (uploadRequestMetadata.processorMetadataList[0].uploadType === PSUploadTypes.SERVER) {
            const progressCallback = (_fileWithMetadata) => {
                let recievedSize = 0;
                const localFileWithMetadata: UploadInProgressFile<any> = _fileWithMetadata;
                return (data: WebsocketResponse) => {
                    this.logger.trace7({ message: 'GoogleDrive file upload progress response', data: data });
                    if (data.uuid === localFileWithMetadata.getFile().name) {
                        recievedSize = parseFloat(data.data) > recievedSize ? parseFloat(data.data) : recievedSize;
                        const progress = 100 * recievedSize / localFileWithMetadata.getFile().size;

                        if (recievedSize === localFileWithMetadata.getFile().size || parseInt(progress + '', 10) === 100) {
                            localFileWithMetadata.setComplete();
                        }

                        if (data.processStageSubState === 'completed') {
                            localFileWithMetadata.setComplete();
                            this.gaService.send(
                                GAContants.HitType.EVENT,
                                GAContants.EventCategory.NETWORK,
                                GAContants.EventAction['FILE-UPLOAD-END'],
                                GAContants.EventLabel.CONVERT,
                                `${localFileWithMetadata.metadata.file.name}`,
                            );
                            this.wsService.unregisterCallback(removableCallback);
                        }

                        if (!localFileWithMetadata.getComplete()) {
                            localFileWithMetadata.setProgress(parseFloat(parseFloat(progress + '').toFixed(2)));
                        } else {
                            localFileWithMetadata.setProgress(parseFloat(parseFloat('100').toFixed(2)));
                        }
                    }
                };
            };
            const removableCallback = progressCallback(fileWithMetadata);
            this.wsService.registerCallback(removableCallback);
            const url = ProcessorMetadataUtils.getUploadEndpoint(uploadRequestMetadata.processorMetadataList[0]) + '/google-drive';
            const metricBase = {
                kind: 'drive' as UploadKind,
                size: fileWithMetadata.getFile().size,
                endpoint: url,
                attempt: uploadOptions?.attempt,
            };
            const startedAt = Date.now();
            this.emitUploadEvent(MetricEventName.FILE_UPLOAD_START, metricBase);
            let result: any;
            try {
                result = await firstValueFrom(this.http.post(url, fileWithMetadata.getFile(), {
                    params: {
                        wsId: this.wsService.getId(),
                        wsDomain: this.wsService.getDomain(),
                        safeFilename: fileWithMetadata.metadata.processorMetadataList[0].fileMetadata.safeName,
                    },
                }));
            } catch (err) {
                this.emitUploadEvent(MetricEventName.FILE_UPLOAD_ERROR, metricBase, {
                    ms: Date.now() - startedAt,
                    ...this.uploadErrorAttributes(err),
                });
                this.wsService.unregisterCallback(removableCallback);
                throw err;
            }
            this.emitUploadEvent(MetricEventName.FILE_UPLOAD_COMPLETE, metricBase, {
                ms: Date.now() - startedAt,
            });

            const info = {
                ftype: getFileMimeType(fileWithMetadata.getFile()),
                fname: fileWithMetadata.getFile().name,
                fsize: fileWithMetadata.getFile().size,
                flastModified: fileWithMetadata.getFile().lastModified,
            };
            const originalInputFiles = result.originalInputFiles[0];
            originalInputFiles.metadata = info;
            return originalInputFiles;
        }
        throw new Error(`Unsupported upload type for Google Drive upload: ${uploadRequestMetadata.processorMetadataList[0].uploadType}`);
    }

    // This is a single file
    public async uploadFileFromLocalDisk(
        fileWithMetadata: UploadInProgressFile<File>,
        progressObserver: Observer<any>,
        uploadOptions?: UploadOptions,
    ): Promise<OriginalInputFile> {
        this.notifyFileUploadStart();
        const uploadRequestMetadata: FileUploadRequestMetadata = fileWithMetadata.getMetadata();
        this.logger.trace7({ message: 'File upload from disk', data: uploadRequestMetadata });
        if (uploadRequestMetadata.processorMetadataList[0].uploadDomain.indexOf('localhost') !== -1) {
            this.uiContext.errorToast('Error', 'Running in Test Mode.', false, false);
        }
        if (!uploadOptions?.suppressGAStart) {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.NETWORK,
                GAContants.EventAction['FILE-UPLOAD-START'],
                GAContants.EventLabel.CONVERT,
                `${fileWithMetadata.metadata.file.name}`,
            );
        }
        if (uploadRequestMetadata.processorMetadataList[0].uploadType === PSUploadTypes.SERVER) {
            const endpointSuffix = this.chunkUploadEnabled ? "res" : "file"
            const fileUploadEndpoint = ProcessorMetadataUtils.getUploadEndpoint(uploadRequestMetadata.processorMetadataList[0]) + `/${endpointSuffix}`;
            const file = fileWithMetadata.getFile();
            const metricBase = {
                kind: (this.chunkUploadEnabled ? 'local_chunk' : 'local_single') as UploadKind,
                size: file.size,
                endpoint: fileUploadEndpoint,
                attempt: uploadOptions?.attempt,
            };
            // Chunk count is derived, not counted: getChunkSize() is
            // deterministic for a given size, so this is the same number
            // Resumable will produce and costs nothing to compute. Omitted
            // entirely on the single-request path (there are no chunks).
            const chunkExtra = this.chunkUploadEnabled
                ? { chunks: Math.max(1, Math.ceil(file.size / this.getChunkSize(file.size))) }
                : {};
            const startedAt = Date.now();
            this.emitUploadEvent(MetricEventName.FILE_UPLOAD_START, metricBase, chunkExtra);
            return this.uploadSingleFile(fileUploadEndpoint, fileWithMetadata.getMetadata(), fileWithMetadata.getFile(), progressObserver, fileWithMetadata, uploadOptions)
                .then((uploadResponse: OriginalInputFile) => {
                    this.emitUploadEvent(MetricEventName.FILE_UPLOAD_COMPLETE, metricBase, {
                        ...chunkExtra,
                        ms: Date.now() - startedAt,
                    });
                    this.gaService.send(
                        GAContants.HitType.EVENT,
                        GAContants.EventCategory.NETWORK,
                        GAContants.EventAction['FILE-UPLOAD-END'],
                        GAContants.EventLabel.CONVERT,
                        `${fileWithMetadata.metadata.file.name}`,
                    );
                    return uploadResponse;
                })
                .catch(err => {
                    // R14-SH14: a cancel is the user's decision, not a failure -- no FILE_UPLOAD_ERROR, no GA
                    // failed hit, no "Could not update the file" dialog. Re-thrown so the caller's await ends.
                    if (isUploadCancelled(err)) {
                        this.logger.trace7({ message: `Disk file upload cancelled ${fileWithMetadata.uuid}`, data: chunkExtra });
                        throw err;
                    }
                    this.emitUploadEvent(MetricEventName.FILE_UPLOAD_ERROR, metricBase, {
                        ...chunkExtra,
                        ms: Date.now() - startedAt,
                        ...this.uploadErrorAttributes(err),
                    });
                    this.logger.error({ message: `Disk file upload failed due to ${err.message}`, error: err });
                    if (!uploadOptions?.suppressGAFailed) {
                        this.gaService.send(
                            GAContants.HitType.EVENT,
                            GAContants.EventCategory.MESSAGE,
                            GAContants.EventAction['FILE-UPLOAD-FAILED'],
                            GAContants.EventLabel.CONVERT,
                            '',
                        );
                    }
                    if (!uploadOptions?.suppressErrorDialog) {
                        // fu-r2 (UXFU1-02/03): the ONE dialog for this failure, from the shared copy table -- never the
                        // server's message, a status code or a class name, and no longer "Could not update the file".
                        // Tool pages never get here: `FileUploadManager.uploadWithRetry` always suppresses this dialog
                        // and reports the failure itself; this is for callers that drive the service directly.
                        const notice = uploadFailureNotice(err) ?? UPLOAD_FAILED_FALLBACK;
                        this.uiContext.showErrorDialog({ title: notice.title, text: notice.text });
                    }
                    throw err;
                });
        }
        // GCP/GCS upload path removed (cost). Uploads always go to the SERVER (ironman edge) path.
        throw new Error(
            `Unsupported upload type: ${uploadRequestMetadata.processorMetadataList[0].uploadType}`,
        )
    }

    private uploadSingleFile(
        url: string, fileMetadata: FileUploadRequestMetadata, file: File, progressObserver: Observer<any>,
        owner?: UploadInProgressFile<File>,
        uploadOptions?: UploadOptions,
    ): Promise<any> {
        if (!this.chunkUploadEnabled) {
            return this.directUploadSingleFile(url, fileMetadata, file, progressObserver)
        }
        // Review 7 S1-10: the chunked path is the one whose resumable.js raised the native alert; an empty file is refused
        // here, before Resumable (see `emptyFileError`). The direct path is unchanged (EDGE13 pins its 0-byte behaviour).
        if (file && file.size === 0) return Promise.reject(emptyFileError(file.name));
        const fn = () => {
            return (resolve, reject) => {
                // HPY NOTE uploaded files should not come here, this service only to upload files
                const info = {
                    ftype: getFileMimeType(file),
                    fname: file.name,
                    fsize: file.size,
                    flastModified: file.lastModified,
                };
                this.chunkUploadSingleFile(url, fileMetadata, file, progressObserver, owner, uploadOptions)
                    .then(accuReply => {
                        resolve(Object.assign(accuReply.originalInputFiles[0], { metadata: info }));
                    }).catch(reject);
            };
        };
        return new Promise(fn());
    }

    private directUploadSingleFile(
        url: string,
        fileMetadata: FileUploadRequestMetadata,
        file: File,
        progressObserver: Observer<any>,
    ) {
        const safeFilename = fileMetadata.processorMetadataList[0].fileMetadata.safeName;
        const uploadUUID = StringUtils.uuid();
        const formData = new FormData();
        formData.append('files', file);

        const info = {
            ftype: getFileMimeType(file),
            fname: file.name,
            fsize: file.size,
            flastModified: file.lastModified,
        };

        return new Promise((resolve, reject) => {
            // Refresh a stale access token BEFORE getCommonHeaders() snapshots the
            // bearer below — otherwise a slow upload could send an expired token and
            // ironman would silently attribute it to ANONYMOUS (§6.3). Best-effort:
            // ensureFreshAccessToken never throws/blocks (flag-off/fresh/anon emits
            // synchronously, preserving today's timing). The header snapshot + send
            // happen inside the subscription so they run AFTER any refresh resolves.
            this.refreshService.ensureFreshAccessToken(url).subscribe(() => {
                const total = file.size
                const { response$, events$ } = this.http.request(
                    url,
                    'POST',
                    formData,
                    {
                        headers: this.http.getCommonHeaders(url),
                        params: {
                            safeName: safeFilename,
                            uuid: uploadUUID,
                        }
                    },

                );
                const eventsSub = events$.subscribe((event: HttpProgressEvent) => {
                    if (event.type === 'upload_progress') {
                        const progress = Math.round(100 * event.event.loaded / total);
                        progressObserver.next(progress);
                    }
                });
                response$.subscribe({
                    next: (accuReply: any) => {
                        eventsSub.unsubscribe();
                        resolve(Object.assign(accuReply.originalInputFiles[0], { metadata: info }));
                        progressObserver.complete()
                    },
                    error: (e) => {
                        eventsSub.unsubscribe();
                        reject(e)
                    }
                })
            });
        })

    }

    private getChunkSize(fileSize: number): number {
        const MB = 1024 * 1024;
        let tierOverride = 0;
        let hardcodedDefault = 0;

        if (fileSize < 1 * MB)        { tierOverride = this.chunkSize1MB;       hardcodedDefault = 100 * 1024; }
        else if (fileSize < 10 * MB)  { tierOverride = this.chunkSize10MB;      hardcodedDefault = 512 * 1024; }
        else if (fileSize < 100 * MB) { tierOverride = this.chunkSize100MB;     hardcodedDefault = 1 * MB; }
        else if (fileSize < 500 * MB) { tierOverride = this.chunkSize500MB;     hardcodedDefault = 2 * MB; }
        else                          { tierOverride = this.chunkSizeLargeFiles; hardcodedDefault = 5 * MB; }

        return (tierOverride || this.chunkSize || hardcodedDefault) + 1;
    }

    private chunkUploadSingleFile(
        url: string,
        fileMetadata: FileUploadRequestMetadata,
        file: File,
        progressObserver: Observer<any>,
        owner?: UploadInProgressFile<File>,
        uploadOptions?: UploadOptions,
    ): Promise<any> {
        const fn = () => {
            const safeFilename = fileMetadata.processorMetadataList[0].fileMetadata.safeName;
            return (resolve: (data) => void, reject: (error) => void) => {
                // Refresh a stale access token BEFORE Resumable's headers (built via
                // getCommonHeaders() below) snapshot the bearer — otherwise a slow
                // chunked upload could send an expired token and ironman would
                // silently attribute it to ANONYMOUS (§6.3). Best-effort: emits
                // synchronously when no refresh is needed (flag-off/fresh/anon),
                // preserving today's timing; the Resumable construction happens
                // inside the subscription so it runs AFTER any refresh resolves.
                this.refreshService.ensureFreshAccessToken(url).subscribe(() => {
                const uploadEndpoint = url;
                const accuReply = {
                    originalInputFiles: [],
                };
                const uploadUUID = StringUtils.uuid();
                let errorOccured = false;
                let cancelled = false;
                // UPLOAD-FINALIZE.md: the promise settles once. resumable.js fires `complete` after a chunk's `error`
                // (the failed file has no chunks left, so nothing is "outstanding"), and again for every sibling chunk
                // that was waiting on its retry timer when the file failed. Each of those used to start the
                // `original-input-files` poll for an upload no edge had all the chunks of (prod: ironman
                // `xc_upload_finalize{outcome="missing"}`, and 403 since 2026-09-29 when no chunk had landed).
                let settled = false;
                // The file's chunk objects as created, for `failedChunkStatus` (resumable.js empties `file.chunks`
                // before it fires `error`).
                let chunkObjects: any[] = [];
                const chunkSize = this.getChunkSize(file.size);
                const r = new Resumable({
                    target: uploadEndpoint,
                    chunkSize,
                    forceChunkSize: true,
                    simultaneousUploads: this.simultaneousUploads,
                    testChunks: false,
                    method: 'octet',
                    prioritizeFirstAndLastChunk: true,
                    maxChunkRetries: 5,
                    chunkRetryInterval: 3000,
                    ...{ permanentErrors: [400, 404, 406, 409, 415, 501] },
                    // fu-finalize: a hung request ends (status 0, retried, then `NetworkError` -> next edge) instead of
                    // never; generous on purpose, see `chunkXhrTimeoutMs`. `forceChunkSize` makes chunkSize the max.
                    // Spread like the line above: @types/resumablejs's ConfigurationHash does not declare it, resumable.js
                    // 1.1.0 reads it (`$.xhr.timeout = $.getOpt('xhrTimeout')`, resumable.js:796).
                    ...{ xhrTimeout: chunkXhrTimeoutMs(chunkSize, this.chunkXhrTimeoutMs) },
                    query: {
                        safeName: safeFilename,
                        uuid: uploadUUID,
                    },
                    maxFileSize: this.maxFileSizeInBytes as any,
                    // Review 7 S1-10: resumable.js's defaults for these two ALERT and then drop the file without a
                    // settle. Both now fail the upload through its normal path (a 0-byte file is refused above already).
                    minFileSizeErrorCallback: (rf: any) => {
                        if (errorOccured || cancelled || settled) return;
                        errorOccured = true;
                        settled = true;
                        reject(emptyFileError(rf?.fileName || rf?.name || file.name));
                    },
                    maxFileSizeErrorCallback: (rf: any) => {
                        if (errorOccured || cancelled || settled) return;
                        errorOccured = true;
                        settled = true;
                        reject(fileTooLargeError(rf?.fileName || rf?.name || file.name, this.maxFileSizeInMB));
                    },
                    // getCommonHeaders() is snapshotted ONCE for the whole file,
                    // so when the requestIdHeader flag is on every chunk of an
                    // upload carries the SAME X-Request-Id. That is the useful
                    // grouping — a chunked upload is one logical operation, and
                    // one id lets Graylog show all of its chunks together.
                    headers: this.http.getCommonHeaders(uploadEndpoint)
                });
                if (!r.support) {
                    alert('Your browser does not support this feature');
                    this.logger.error({ message: 'Your browser does not support this feature', data: { url, } });
                } // TODO: do better error handling

                // R14-SH14: retain the handle and hand the in-progress file a way to reach it. `cancelled` is
                // raised BEFORE `r.cancel()` because resumable.js fires `fileProgress` -- and, through
                // uploadNextChunk, `complete` -- on the way down; every handler below checks it first.
                const release = () => { this.chunkUploads.delete(uploadUUID); };
                this.chunkUploads.set(uploadUUID, {
                    resumable: r,
                    cancel: () => {
                        if (cancelled) return;
                        cancelled = true;
                        settled = true;
                        release();
                        try {
                            r.cancel();
                        } catch (e) {
                            this.logger.error({ message: `Error thrown by file uploader on cancel ${uploadUUID}`, error: e, data: { uploadUUID } });
                        }
                        this.logger.trace7({ message: `Chunk file upload cancelled ${uploadUUID}`, data: { url, uploadUUID } });
                        reject(new UploadCancelledError(owner?.uuid ?? uploadUUID));
                    },
                });
                if (owner && typeof owner.bindCancel === 'function') {
                    owner.bindCancel(() => { this.cancelChunkUpload(uploadUUID); });
                }

                r.on('fileProgress', (f, message) => {
                    if (cancelled) return;
                    const progress = r.progress();
                    progressObserver.next(parseFloat(parseFloat((progress * 100) + '').toFixed(2)));
                });

                r.on('fileSuccess', (f, message) => {
                    if (cancelled) return;
                    this.logger.trace7({
                        message: `Chunk file upload succcessfully ended. ${describeResponseBody(message)}`,
                    });
                    try {
                        if (isString(message)) {
                            const parsed = JSON.parse(message);
                            if (parsed.originalInputFiles) {
                                accuReply.originalInputFiles.push(...parsed.originalInputFiles);
                            }
                            // If response is {"Uploaded":...} (non-completion chunk finished last), skip it
                        }
                    } catch (e) {
                        // FU1-04: a 200 that is not JSON is someone else's page (a captive portal); never log the body.
                        this.logger.fatal({
                            message: 'Error thrown while generating accuReply.originalInputFiles: ' + describeResponseBody(message) + ` ${uploadUUID}`,
                            error: e,
                            data: { url, uploadUUID }
                        });
                        errorOccured = true;
                    }
                });

                r.on('complete', (f, message) => {
                    // `settled`: the upload already failed (a chunk error, which rejected) or this is a repeat
                    // `complete`. Only a run where every chunk was answered 200 gets here first, and only then can the
                    // result be on the edge: the reply that completed the file was not the last to arrive, or did not
                    // parse (`errorOccured`), so ask for it.
                    if (cancelled || settled) return;
                    settled = true;
                    release();
                    if (errorOccured || accuReply.originalInputFiles.length === 0) {
                        this.updateFileUploadFileOriginalInputFilesAndResolve(url, uploadUUID, safeFilename, accuReply, progressObserver, resolve, reject);
                    } else {
                        this.logger.trace7({
                            message: `Chunk file upload completed. ${message}`,
                        });
                        progressObserver.complete();
                        resolve(accuReply);
                    }
                });

                r.on('catchAll', (event, ...args) => {
                    //console.log(event, args);
                });

                r.on('cancel', () => {
                    // Reached only from the retained handle's `cancel` above, which has already released.
                    release();
                });

                r.on('fileAdded', (f, event) => {
                    chunkObjects = Array.isArray(f?.chunks) ? f.chunks.slice() : [];
                    r.upload();
                });

                r.on('error', (e, rfile) => {
                    // A second `error` comes from a sibling chunk that was waiting to retry when the file failed.
                    if (cancelled || settled) return;
                    settled = true;
                    release();
                    const status = failedChunkStatus(chunkObjects);
                    const err = chunkFailureError(e, status);
                    // FU1-04: never the response body itself -- a proxy's block page can carry the user's name, email,
                    // IP and the blocked URL (FIX-7 hashes PII; this line used to undo it). Status, body class, at most
                    // the page title.
                    const body = describeResponseBody(e);
                    this.logger.error({ error: err, message: ` Error thrown by file uploader: status=${status ?? 'unknown'} ${body} ${uploadUUID}`, data: { uploadUUID, status: status ?? null, body } });
                    errorOccured = true;
                    reject(err);
                });

                r.addFile(file);
                });
            };
        };
        return new Promise(fn());
    }

    private updateFileUploadFileOriginalInputFilesAndResolve(url, uploadUUID, safeFilename, accuReply, progressObserver, resolve, reject) {
        this.http.get<{ [key: string]: [] }>(url + '/original-input-files', {
            params: {
                uuid: uploadUUID,
                safeName: safeFilename
            },
        }).pipe(
            mergeMap((resp) => {
                if (!resp || !resp.originalInputFiles) {
                    return of({}).pipe(
                        delay(2000),
                        mergeMap(() => {
                            return throwError(() => {
                                const error = new Error(`Request to get original uploaded file data returned null. ${uploadUUID} ${resp} ${url}`);
                                // The chunk-upload finalize answered 200 with no
                                // data ("Fallback GET found no data" on east-32,
                                // ironman xc_upload_finalize{outcome="missing"}).
                                // No status code can express it, so the class is
                                // carried on the error itself and read by
                                // uploadErrorAttributes() — otherwise a measured
                                // prod bug would be filed under `unknown`.
                                error[FINALIZE_MISSING_MARKER] = true;
                                return error;
                            });
                        }),
                    );
                }
                return of(resp)
            }),
            retry({ count: 3, delay: 2000 }),
        ).subscribe({
            next: (resp: any) => {
                this.logger.trace7({ message: `Chunk upload completed ${uploadUUID}`, data: { url, resp } });
                accuReply.originalInputFiles.push(...resp.originalInputFiles);
                progressObserver.complete();
                resolve(accuReply);
            },
            error: (err) => {
                reject(err);
                this.logger.error({ message: `Error file upload through chunk upload.${uploadUUID}`, error: err, data: { url, } });
                // fu-r2 (UXFU1-03): no dialog here any more. This rejection always reaches `uploadFileFromLocalDisk`'s
                // catch, which shows the one dialog for it (finalize-missing -> the "our side" copy) unless the caller
                // suppressed it -- this one used to stack under it.
            },
        });
    }
}
